import React from "react";
import { message, Upload } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import { Trans } from "react-i18next";
import IconCamera from "@assets/imgs/icon-camera.png";
import UserAvatar from "./UserAvatar";

interface AvatarUploadProps {
  avatarUrl?: string;
  userName?: string;
  size?: number;
  className?: string;
  onUploadSuccess?: (url: string) => void;
  onUploadError?: (error: Error) => void;
  uploadAction: (file: File) => Promise<string>;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const AvatarUpload: React.FC<AvatarUploadProps> = ({ size = 40, onUploadSuccess, onUploadError, uploadAction }) => {
  const beforeUpload = (file: RcFile): boolean => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/jpg";
    if (!isJpgOrPng) {
      message.error(<Trans>Only JPG/PNG/JPEG file format is allowed for avatar upload!</Trans>);
      return false;
    }
    const isLimitSize = file.size <= MAX_FILE_SIZE;
    if (!isLimitSize) {
      message.error(<Trans>Avatar image must be smaller than 5MB!</Trans>);
      return false;
    }
    return true;
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    try {
      const url = await uploadAction(file as File);
      onUploadSuccess?.(url);
      onSuccess?.(url);
    } catch (error) {
      const err = error as Error;
      onUploadError?.(err);
      onError?.(err);
    }
  };

  return (
    <Upload
      name="avatar"
      className={`cursor-pointer w-[${size}px] h-[${size}px] relative group`}
      showUploadList={false}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
    >
      <UserAvatar className="w-[56px] h-[56px] "></UserAvatar>
      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <img src={IconCamera} alt="Upload" className="w-full h-full" />
      </div>
    </Upload>
  );
};

export default AvatarUpload;
